import { Box, type BoxProps, Flex, Spacer, Stack, Text } from '@chakra-ui/react'
import { faFontCase } from '@fortawesome/pro-regular-svg-icons'
import React, {
  type CSSProperties,
  forwardRef,
  type ReactElement,
  useCallback,
  useRef,
  useState,
} from 'react'
import { v4 as uuidv4 } from 'uuid'

import { noOp } from '@/utils/helpers'

import { useToaster } from '@/components/Toast/useToaster'
import { withErrorBoundary } from '@/components/Worlds/UI/ErrorBoundary'

import MentionSuggestionList from '../internal/Mention/MentionSuggestionList'
import { MentionsContextProvider } from '../internal/Mention/MentionsContextProvider'
import RichTextEditorBase from '../internal/RichTextEditorBase'
import ToolbarButton from '../internal/ToolbarButton'
import {
  BoldButton,
  BulletedListButton,
  EmojiButton,
  ImageUploadButton,
  ItalicButton,
  LinkButton,
  MathButton,
  MicrophoneButton,
  type MicrophoneButtonRef,
  NumberedListButton,
  UnderlineButton,
} from '../internal/ToolbarButtons'
import type { RichTextEditorProps } from '../internal/types'
import type { RichTextEditorRef } from '../types'
import FileUploadPreview from './FileUploadPreview'
import type { FileUpload, RichTextEditorSimpleRef } from './types'

function Toolbar({
  hideListFormatting = false,
}: {
  hideListFormatting?: boolean
}): React.ReactElement {
  return (
    <Stack direction="row" align="center" spacing="4px">
      <BoldButton />
      <ItalicButton />
      <UnderlineButton />
      <LinkButton />
      <MathButton />
      {hideListFormatting === true ? null : (
        <>
          <BulletedListButton />
          <NumberedListButton />
        </>
      )}
    </Stack>
  )
}

const MAX_FILE_SIZE_MB = 3
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024
const MAX_FILES_ALLOWED = 5

const RichTextEditorSimpleInternal = forwardRef<
  RichTextEditorSimpleRef,
  RichTextEditorProps & {
    rightFooterContent?: () => ReactElement
    leftFooterContent?: () => ReactElement
    hideImageUpload?: boolean
    hideFormatting?: boolean
    hideListFormatting?: boolean
    containerProps?: BoxProps
    offsetEmojiPopover?: boolean
    minHeight?: CSSProperties['minHeight']
  }
>(
  (
    {
      leftFooterContent,
      rightFooterContent,
      hideImageUpload = false,
      hideFormatting = false,
      hideListFormatting = false,
      showMicrophone = false,
      mentions,
      containerProps,
      offsetEmojiPopover = false,
      minHeight,
      ...props
    },
    ref
  ): ReactElement => {
    const toast = useToaster()
    const editorRef = useRef<RichTextEditorRef>(null)
    const microphoneRef = useRef<MicrophoneButtonRef | null>(null)
    const [showFormatting, setShowFormatting] = useState(false)
    const [uploads, setUploads] = useState<FileUpload[]>([])

    const { disabled = false } = props

    const onFilesUpload = useCallback(
      (files: File[]) => {
        if (hideImageUpload || disabled) {
          return
        }
        if (files.some(file => file.size >= MAX_FILE_SIZE_BYTES)) {
          toast({
            title: `Please upload files at most ${MAX_FILE_SIZE_MB}MB in size`,
            status: 'error',
          })
          return
        }
        if (uploads.length + files.length > MAX_FILES_ALLOWED) {
          toast({
            title: `Please upload no more than ${MAX_FILES_ALLOWED} files`,
            status: 'error',
          })
          return
        }
        setUploads(currentUploads => [
          ...currentUploads,
          ...files.map(file => ({ file, uniqueIdentifier: uuidv4() })),
        ])
      },
      [hideImageUpload, disabled, uploads.length, toast]
    )

    React.useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          editorRef.current?.clear()
          microphoneRef.current?.stopRecording().catch(noOp)
          setUploads([])
        },
        focus: () => editorRef.current?.focus(),
        getUploads: () => uploads.map(({ file }) => file),
        setValue: value => editorRef.current?.setValue(value),
      }),
      [editorRef, uploads]
    )

    const textEditorComponent = (
      <Box borderRadius="8px" border="1px solid" borderColor="border" bg="bg" {...containerProps}>
        <RichTextEditorBase
          ref={editorRef}
          {...props}
          onFilesUpload={onFilesUpload}
          mentions={mentions}
          header={() => (
            <Box position="relative">
              {mentions != null ? <MentionSuggestionList /> : null}
              {showFormatting ? (
                <Flex direction="row" align="center" px="8px" py="8px">
                  <Toolbar hideListFormatting={hideListFormatting} />
                </Flex>
              ) : null}
            </Box>
          )}
          footer={() => (
            <>
              <FileUploadPreview
                disabled={disabled}
                uploads={uploads}
                onDeleteUpload={(uniqueIdentifier: string) =>
                  setUploads(currentUploads =>
                    currentUploads.filter(upload => upload.uniqueIdentifier !== uniqueIdentifier)
                  )
                }
              />
              <Flex px="8px" py="8px" justify="space-between">
                <Stack direction="row" align="center" spacing="4px">
                  <EmojiButton isPopoverOffset={offsetEmojiPopover} />
                  {hideImageUpload ? null : <ImageUploadButton onUpload={onFilesUpload} />}
                  {hideFormatting ? null : (
                    <ToolbarButton
                      active={false}
                      icon={faFontCase}
                      onMouseDown={() => setShowFormatting(x => !x)}
                    />
                  )}
                  {showMicrophone ? <MicrophoneButton parentRef={microphoneRef} /> : null}
                  {leftFooterContent?.()}
                </Stack>
                <Spacer />
                {rightFooterContent?.()}
              </Flex>
            </>
          )}
          style={{
            minHeight,
            maxHeight: '300px',
            overflowY: 'scroll',
            padding: '16px',
            backgroundColor: disabled ? 'bg.subtle' : 'bg',
            overflowX: 'hidden',
            borderRadius: '8px',
          }}
        />
      </Box>
    )

    if (mentions != null) {
      return (
        <MentionsContextProvider
          usersInThread={mentions.usersInThread ?? []}
          getMentionSuggestions={mentions.getMentionSuggestions}
        >
          {textEditorComponent}
        </MentionsContextProvider>
      )
    }

    return textEditorComponent
  }
)

const RichTextEditorSimple = withErrorBoundary(RichTextEditorSimpleInternal, () => (
  <Text>Oops! You cannot edit this content right now.</Text>
))

export default RichTextEditorSimple
